import React from "react"
import { useQueryClient } from "@tanstack/react-query"
import { usePolling, getPollingQueryOpts } from "../layout/HoaAdminLayout"
import { useToast } from "@/hooks/use-toast"
import { useLanguage } from "@/contexts/LanguageContext"
import {
  usePolls,
  usePoll,
  useCreatePoll,
  useUpdatePoll,
  useDeletePoll,
  useClosePoll,
} from "@/api/polls"
import type {
  Poll as APIPoll,
  PollOption as APIPollOption,
  CreatePollData,
  UpdatePollData,
} from "@/types/poll"
import { useBuildings } from "@/hooks/useBuildings"

/** Tokens */
const PRIMARY = "#1F3A93"
/** Quiet outline: white bg + black text, visible on glass */
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50"

/** Types - Map backend types to UI types */
type VoteOption = { id: string; label: string; votes: number }
type PollStatus = "SCHEDULED" | "LIVE" | "CLOSED"

type Poll = {
  id: string
  title: string
  description?: string
  options: VoteOption[]
  status: PollStatus
  startAt?: string // ISO
  endAt?: string   // ISO
  allowMultiple: boolean
  anonymous: boolean
  allowChangeVote: boolean
  quorumPercent?: number // 0-100
  totalEligible?: number
  createdBy: string
  createdAt: string
}

/** Helper: Map backend poll to UI poll */
function mapBackendToUI(apiPoll: APIPoll): Poll {
  const now = new Date()
  const startsAt = new Date(apiPoll.starts_at)
  const endsAt = apiPoll.ends_at ? new Date(apiPoll.ends_at) : null
  
  // Determine UI status
  let uiStatus: PollStatus = "CLOSED"
  if (apiPoll.status === "active") {
    if (startsAt > now) {
      uiStatus = "SCHEDULED"
    } else if (!endsAt || endsAt > now) {
      uiStatus = "LIVE"
    } else {
      uiStatus = "CLOSED"
    }
  }
  
  return {
    id: apiPoll.id,
    title: apiPoll.question,
    description: apiPoll.description || undefined,
    options: apiPoll.options.map(opt => ({
      id: opt.id,
      label: opt.text,
      votes: opt.vote_count,
    })),
    status: uiStatus,
    startAt: apiPoll.starts_at,
    endAt: apiPoll.ends_at || undefined,
    allowMultiple: apiPoll.allow_multiple_votes,
    anonymous: true, // Backend doesn't have this yet
    allowChangeVote: apiPoll.allow_multiple_votes,
    quorumPercent: undefined, // Backend doesn't have this yet
    totalEligible: undefined, // Backend doesn't have this yet
    createdBy: `${apiPoll.created_by.first_name} ${apiPoll.created_by.last_name}`,
    createdAt: apiPoll.created_at,
  }
}

/** ----------------------------------------------------------------
 * Helper Functions
 * ----------------------------------------------------------------*/
function mapUIToBackendCreate(uiData: {
  building: string
  title: string
  description?: string
  options: { label: string }[]
  status: PollStatus
  startAt?: string
  endAt?: string
  allowMultiple: boolean
  anonymous: boolean
  allowChangeVote: boolean
  quorumPercent?: number
  totalEligible?: number
}): CreatePollData {
  return {
    building: uiData.building, // Required FK to Building/Condo
    question: uiData.title,
    description: uiData.description,
    status: uiData.status === "CLOSED" ? "closed" : "active",
    starts_at: uiData.startAt,
    ends_at: uiData.endAt || null,
    allow_multiple_votes: uiData.allowMultiple,
    show_results_before_close: false,
    options: uiData.options.map((opt, idx) => ({
      text: opt.label,
      order: idx,
    })),
  }
}

function mapUIToBackendUpdate(uiData: Partial<Poll>): UpdatePollData {
  const update: UpdatePollData = {}
  
  if (uiData.title) update.question = uiData.title
  if (uiData.description !== undefined) update.description = uiData.description
  if (uiData.status) {
    update.status = uiData.status === "CLOSED" ? "closed" : "active"
  }
  if (uiData.startAt !== undefined) update.starts_at = uiData.startAt
  if (uiData.endAt !== undefined) update.ends_at = uiData.endAt || null
  if (uiData.allowMultiple !== undefined) {
    update.allow_multiple_votes = uiData.allowMultiple
  }
  
  return update
}

/** ----------------------------------------------------------------
 * Page
 * ----------------------------------------------------------------*/
export default function PollingPage() {
  const { toast } = useToast()
  const { t } = useLanguage()
  const [showCreate, setShowCreate] = React.useState(false)
  const [viewing, setViewing] = React.useState<Poll | null>(null)
  const [confirmDelete, setConfirmDelete] = React.useState<Poll | null>(null)
  const [confirmClose, setConfirmClose] = React.useState<Poll | null>(null)
  const [editing, setEditing] = React.useState<Poll | null>(null)

  // Fetch polls from backend
  const { data: apiData, isLoading, isFetching, refetch } = usePolls()

  // Mutations
  const createMutation = useCreatePoll()
  const updateMutation = useUpdatePoll()
  const deleteMutation = useDeletePoll()
  const closeMutation = useClosePoll()

  // Map backend data to UI format
  const items = React.useMemo(() => {
    if (!apiData) return []
    // Note: usePolls returns PollListItem[] which doesn't have full Poll data
    // We create a minimal Poll for list view
    const mapped = apiData.map((item): Poll => {
      const now = new Date()
      const startsAt = item.starts_at ? new Date(item.starts_at) : null
      const endsAt = item.ends_at ? new Date(item.ends_at) : null
      
      // Determine UI status
      let uiStatus: PollStatus = "CLOSED"
      if (item.status === "active") {
        if (startsAt && startsAt > now) {
          uiStatus = "SCHEDULED"
        } else if (!endsAt || endsAt > now) {
          uiStatus = "LIVE"
        }
      }
      
      return {
        id: item.id,
        title: item.question,
        description: '', // Not available in list view
        options: [], // Not available in list view, will fetch when viewing
        status: uiStatus,
        startAt: item.starts_at || undefined,
        endAt: item.ends_at || undefined,
        allowMultiple: false, // Default
        anonymous: true, // Default
        allowChangeVote: false, // Default
        quorumPercent: undefined,
        totalEligible: undefined,
        createdBy: `${item.created_by.first_name} ${item.created_by.last_name}`,
        createdAt: item.created_at,
      }
    })
    // Sort: LIVE first, then SCHEDULED, then CLOSED
    const order = { LIVE: 0, SCHEDULED: 1, CLOSED: 2 } as const
    return mapped.sort((a, b) => order[a.status] - order[b.status])
  }, [apiData])

  const live = items.filter(p => p.status === "LIVE").length
  const scheduled = items.filter(p => p.status === "SCHEDULED").length
  const closed = items.filter(p => p.status === "CLOSED").length

  // Handle create
  const handleCreate = async (uiData: Parameters<typeof mapUIToBackendCreate>[0]) => {
    try {
      const backendData = mapUIToBackendCreate(uiData)
      await createMutation.mutateAsync(backendData)
      setShowCreate(false)
      toast({
        title: t("hoa.polls.toast.createdTitle"),
        description: t("hoa.polls.toast.createdDesc"),
      })
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || t("hoa.polls.toast.createFailed"),
      })
    }
  }

  // Handle update
  const handleUpdate = async (id: string, uiData: Partial<Poll>) => {
    try {
      const backendData = mapUIToBackendUpdate(uiData)
      await updateMutation.mutateAsync({ id, data: backendData })
      setEditing(null)
      toast({
        title: t("hoa.polls.toast.updatedTitle"),
        description: t("hoa.polls.toast.updatedDesc"),
      })
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || t("hoa.polls.toast.updateFailed"),
      })
    }
  }

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id)
      setConfirmDelete(null)
      toast({
        title: t("hoa.polls.toast.deletedTitle"),
        description: t("hoa.polls.toast.deletedDesc"),
      })
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || t("hoa.polls.toast.deleteFailed"),
      })
    }
  }

  // Handle close
  const handleClose = async (id: string) => {
    try {
      await closeMutation.mutateAsync(id)
      setConfirmClose(null)
      toast({
        title: t("hoa.polls.toast.closedTitle"),
        description: t("hoa.polls.toast.closedDesc"),
      })
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message || t("hoa.polls.toast.closeFailed"),
      })
    }
  }

  return (
    <div className="relative space-y-6 sm:space-y-7">
      {/* ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* header / actions (glass) */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-2xl bg-white/80 backdrop-blur ring-1 ring-black/5 p-4">
        <div>
          <h2 className="text-[18px] font-semibold text-gray-900">
            {t("hoa.polls.title")}
          </h2>
          <p className="text-sm text-gray-600">
            {t("hoa.polls.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-600">
            {isLoading
              ? t("common.loading")
              : t("hoa.polls.statusSummary", { live, scheduled, closed })}
          </span>
          <button
            className={`px-3 py-2 text-sm ${QUIET}`}
            onClick={() => refetch()}
          >
            {isFetching ? t("common.refreshing") : t("common.refresh")}
          </button>
          <button
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px]"
            style={{ background: "linear-gradient(90deg,#1F3A93 0%,#1F3A93 70%,#27AE60 100%)" }}
            onClick={() => setShowCreate(true)}
          >
            {t("hoa.polls.new")}
          </button>
        </div>
      </div>

      {/* list */}
      <div className="space-y-4">
        {isLoading ? (
          <SkeletonPoll />
        ) : items.length === 0 ? (
          <EmptyState onNew={() => setShowCreate(true)} />
        ) : (
          items.map((p) => (
            <PollCard
              key={p.id}
              poll={p}
              onView={() => setViewing(p)}
              onEdit={() => setEditing(p)}
              onClose={() => setConfirmClose(p)}
              onDelete={() => setConfirmDelete(p)}
            />
          ))
        )}
      </div>

      {/* Create modal (glass) */}
      {showCreate && (
        <CreatePollModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
          creating={createMutation.isPending}
        />
      )}

      {/* Edit modal (glass) */}
      {editing && (
        <EditPollModal
          poll={editing}
          onClose={() => setEditing(null)}
          onSave={(patch) => handleUpdate(editing.id, patch)}
          saving={updateMutation.isPending}
        />
      )}

      {/* Details modal (glass) */}
      {viewing && <PollDetailsModal poll={viewing} onClose={() => setViewing(null)} />}

      {/* Close + Delete confirmations (glass) */}
      {confirmClose && (
        <ConfirmModal
          tone="warning"
          title={t("hoa.polls.closeTitle")}
          message={t("hoa.polls.closeMessage", { title: confirmClose.title })}
          confirmLabel={t("hoa.polls.closeAction")}
          confirming={closeMutation.isPending}
          onCancel={() => setConfirmClose(null)}
          onConfirm={() => handleClose(confirmClose.id)}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          tone="danger"
          title={t("hoa.polls.deleteTitle")}
          message={t("hoa.polls.deleteMessage", { title: confirmDelete.title })}
          confirmLabel={t("common.delete")}
          confirming={deleteMutation.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete.id)}
        />
      )}
    </div>
  )
}

/** ----------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------*/
function PollCard({
  poll,
  onView,
  onEdit,
  onClose,
  onDelete,
}: {
  poll: Poll
  onView: () => void
  onEdit: () => void
  onClose: () => void
  onDelete: () => void
}) {
  const { t } = useLanguage()
  const totalVotes = poll.options.reduce((a, b) => a + (b.votes ?? 0), 0)
  const leftColor =
    poll.status === "LIVE" ? "#27AE60"
    : poll.status === "SCHEDULED" ? "#F59E0B"
    : "#9CA3AF"

  const quorumReached =
    poll.quorumPercent && poll.totalEligible
      ? (totalVotes / poll.totalEligible) * 100 >= poll.quorumPercent
      : undefined

  return (
    <div className="relative rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-5 sm:p-6 shadow-lg shadow-black/10">
      {/* status rail */}
      <span className="absolute left-0 top-0 h-full w-1.5 rounded-l-3xl" style={{ background: leftColor }} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="truncate text-[16px] font-semibold text-gray-900">{poll.title}</h3>
          {poll.description && (
            <p className="text-sm text-gray-700/90 line-clamp-2">{poll.description}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <Chip tone={poll.status}>
              {poll.status === "LIVE"
                ? t("hoa.polls.status.live")
                : poll.status === "SCHEDULED"
                ? t("hoa.polls.status.scheduled")
                : t("hoa.polls.status.closed")}
            </Chip>
            {poll.allowMultiple && <Chip>{t("hoa.polls.multipleChoice")}</Chip>}
            {poll.anonymous && <Chip>{t("hoa.polls.anonymous")}</Chip>}
            {typeof poll.quorumPercent === "number" && (
              <Chip>
                {t("hoa.polls.quorum", { percent: poll.quorumPercent })}
                {quorumReached !== undefined && (quorumReached ? " ✓" : " ✕")}
              </Chip>
            )}
            {poll.startAt && (
              <span>{t("hoa.polls.starts", { date: fmtDateTime(poll.startAt) })}</span>
            )}
            {poll.endAt && (
              <span>{t("hoa.polls.ends", { date: fmtDateTime(poll.endAt) })}</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className={`px-3 py-2 text-sm ${QUIET}`} onClick={onView}>
            {t("common.viewDetails")}
          </button>
          <button className={`px-3 py-2 text-sm ${QUIET}`} onClick={onEdit}>
            {t("common.edit")}
          </button>
          {poll.status !== "CLOSED" && (
            <button
              className={`px-3 py-2 text-sm ${QUIET}`}
              onClick={onClose}
              title={t("hoa.polls.closeAction")}
            >
              {t("hoa.polls.closeAction")}
            </button>
          )}
          <button
            className={`px-3 py-2 text-sm ${QUIET} hover:!bg-red-50`}
            onClick={onDelete}
            title={t("common.delete")}
          >
            {t("common.delete")}
          </button>
        </div>
      </div>

      {/* results (neutral bars) */}
      <div className="mt-4 space-y-3">
        {poll.options.map((o) => {
          const pct = totalVotes ? Math.round((o.votes / totalVotes) * 100) : 0
          return (
            <div key={o.id}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-900">{o.label}</span>
                <span className="text-gray-600">{o.votes} ({pct}%)</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-[#E9ECEF]">
                <div
                  className="h-2 rounded-full"
                  style={{ width: `${pct}%`, background: "#111827" }}
                />
              </div>
            </div>
          )
        })}
        <div className="mt-2 text-xs text-gray-600">
          {t("hoa.polls.totalVotes", { count: totalVotes })}
        </div>
      </div>
    </div>
  )
}

function PollDetailsModal({ poll, onClose }: { poll: Poll; onClose: () => void }) {
  const { t } = useLanguage()
  // Fetch full poll data with options (list view doesn't include options)
  const { data: fullPoll, isLoading } = usePoll(poll.id)
  
  // Use options from fullPoll if available, otherwise show from the list view poll
  const options = fullPoll?.options || poll.options
  const totalVotes = options.reduce((a, b: any) => a + (b.votes ?? b.vote_count ?? 0), 0)
  
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2">
        <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[16px] font-semibold text-gray-900">{poll.title}</h3>
            <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>
              {t("common.close")}
            </button>
          </div>
          {poll.description && <p className="text-sm text-gray-700/90">{poll.description}</p>}
          
          {isLoading && options.length === 0 && (
            <div className="my-4 text-center text-sm text-gray-500">
              {t("hoa.polls.loadingDetails")}
            </div>
          )}

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 text-xs text-gray-600">
            <div>
              <b>{t("common.status")}:</b>{" "}
              {poll.status === "LIVE"
                ? t("hoa.polls.status.live")
                : poll.status === "SCHEDULED"
                ? t("hoa.polls.status.scheduled")
                : t("hoa.polls.status.closed")}
            </div>
            <div><b>{t("hoa.polls.anonymous")}:</b> {poll.anonymous ? t("common.yes") : t("common.no")}</div>
            <div><b>{t("hoa.polls.allowMultiple")}:</b> {poll.allowMultiple ? t("common.yes") : t("common.no")}</div>
            <div><b>{t("hoa.polls.allowChangeVote")}:</b> {poll.allowChangeVote ? t("common.yes") : t("common.no")}</div>
            {poll.quorumPercent !== undefined && <div><b>{t("hoa.polls.quorumLabel")}:</b> {poll.quorumPercent}%</div>}
            {poll.totalEligible !== undefined && <div><b>{t("hoa.polls.eligible")}:</b> {poll.totalEligible}</div>}
            {poll.startAt && <div><b>{t("hoa.polls.startsLabel")}:</b> {fmtDateTime(poll.startAt)}</div>}
            {poll.endAt && <div><b>{t("hoa.polls.endsLabel")}:</b> {fmtDateTime(poll.endAt)}</div>}
            <div className="sm:col-span-2">
              <b>{t("hoa.polls.createdBy")}:</b> {poll.createdBy} • {fmtDateTime(poll.createdAt)}
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {options.length > 0 ? (
              options.map((o: any) => {
                const votes = o.votes ?? o.vote_count ?? 0
                const pct = totalVotes ? Math.round((votes / totalVotes) * 100) : 0
                return (
                  <div key={o.id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-900">{o.label || o.text}</span>
                      <span className="text-gray-600">{votes} ({pct}%)</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-[#E9ECEF]">
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${pct}%`, background: "#111827" }}
                      />
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-sm text-gray-500">{t("hoa.polls.noOptions")}</div>
            )}
            <div className="mt-2 text-xs text-gray-600">
              {t("hoa.polls.totalVotes", { count: totalVotes })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Edit Poll Modal (prefilled) */
function EditPollModal({
  poll,
  onClose,
  onSave,
  saving,
}: {
  poll: Poll
  onClose: () => void
  onSave: (patch: Partial<Poll>) => void
  saving: boolean
}) {
  const { t } = useLanguage()
  const [title, setTitle] = React.useState(poll.title)
  const [description, setDescription] = React.useState(poll.description ?? "")
  // safe option editing: change labels; do not change vote counts
  const [labels, setLabels] = React.useState<string[]>(poll.options.map(o => o.label))
  const [startAt, setStartAt] = React.useState<string>(poll.startAt ? toLocalInput(poll.startAt) : "")
  const [endAt, setEndAt] = React.useState<string>(poll.endAt ? toLocalInput(poll.endAt) : "")
  const [status, setStatus] = React.useState<PollStatus>(poll.status)
  const [anonymous, setAnonymous] = React.useState(poll.anonymous)
  const [allowMultiple, setAllowMultiple] = React.useState(poll.allowMultiple)
  const [allowChangeVote, setAllowChangeVote] = React.useState(poll.allowChangeVote)
  const [quorumPercent, setQuorumPercent] = React.useState<number | "">(poll.quorumPercent ?? "")
  const [totalEligible, setTotalEligible] = React.useState<number | "">(poll.totalEligible ?? "")

  function addOption() {
    setLabels(arr => [...arr, t("hoa.polls.optionLabel", { index: arr.length + 1 })])
  }
  function removeOption(i: number) {
    setLabels(arr => (arr.length <= 2 ? arr : arr.filter((_, idx) => idx !== i)))
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim() || labels.filter(l => l.trim()).length < 2) return
    onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      status,
      anonymous,
      allowMultiple,
      allowChangeVote,
      startAt: startAt ? new Date(startAt).toISOString() : undefined,
      endAt: endAt ? new Date(endAt).toISOString() : undefined,
      quorumPercent: quorumPercent === "" ? undefined : Number(quorumPercent),
      totalEligible: totalEligible === "" ? undefined : Number(totalEligible),
      options: labels.filter(l => l.trim()).map((label) => ({ id: "", label, votes: 0 })), // id/votes preserved server-side
    })
  }

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-2xl">
        <form onSubmit={submit} className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
          <div className="flex items-center justify-between">
            <h3 className="text-[16px] font-semibold text-gray-900">
              {t("hoa.polls.editTitle")}
            </h3>
            <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
              {t("common.close")}
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="text-sm">{t("common.title")}</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                placeholder={t("hoa.polls.titlePlaceholder")}
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm">{t("hoa.polls.descriptionOptional")}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                rows={3}
                placeholder={t("hoa.polls.descriptionPlaceholder")}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm">{t("hoa.polls.optionsLabel")}</label>
              <div className="mt-2 space-y-2">
                {labels.map((lbl, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={lbl}
                      onChange={(e) => setLabels(arr => arr.map((x, idx) => idx === i ? e.target.value : x))}
                      className="w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                      placeholder={t("hoa.polls.optionLabel", { index: i + 1 })}
                    />
                    <button
                      type="button"
                      className={`px-2 py-2 text-sm ${QUIET}`}
                      onClick={() => removeOption(i)}
                      disabled={labels.length <= 2}
                      title={t("hoa.polls.removeOption")}
                    >
                      −
                    </button>
                  </div>
                ))}
                <button type="button" className={`px-3 py-2 ${QUIET}`} onClick={addOption}>
                  {t("hoa.polls.addOption")}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm">{t("hoa.polls.start")}</label>
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>
            <div>
              <label className="text-sm">{t("hoa.polls.end")}</label>
              <input
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>

            <div>
              <label className="text-sm">{t("common.status")}</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as PollStatus)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              >
                <option value="SCHEDULED">{t("hoa.polls.status.scheduled")}</option>
                <option value="LIVE">{t("hoa.polls.status.live")}</option>
                <option value="CLOSED">{t("hoa.polls.status.closed")}</option>
              </select>
            </div>

            <div>
              <label className="text-sm">{t("hoa.polls.quorumOptional")}</label>
              <input
                type="number"
                min={0}
                max={100}
                value={quorumPercent}
                onChange={(e) => setQuorumPercent(e.target.value === "" ? "" : Number(e.target.value))}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                placeholder={t("hoa.polls.quorumPlaceholder")}
              />
            </div>

            <div>
              <label className="text-sm">{t("hoa.polls.totalEligibleOptional")}</label>
              <input
                type="number"
                min={0}
                value={totalEligible}
                onChange={(e) => setTotalEligible(e.target.value === "" ? "" : Number(e.target.value))}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>

            <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="h-4 w-4" />
                {t("hoa.polls.anonymousVoting")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} className="h-4 w-4" />
                {t("hoa.polls.allowMultiple")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allowChangeVote} onChange={(e) => setAllowChangeVote(e.target.checked)} className="h-4 w-4" />
                {t("hoa.polls.allowChangeVote")}
              </label>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button type="button" className={`px-4 py-2 text-sm ${QUIET}`} onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px]"
              style={{ background: "linear-gradient(90deg, #1F3A93 0%, #1F3A93 70%, #27AE60 100%)" }}
            >
              {saving ? t("common.saving") : t("common.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** Generic confirm modal (warning/danger) */
function ConfirmModal({
  tone, title, message, confirmLabel, onCancel, onConfirm, confirming,
}: {
  tone: "warning" | "danger"
  title: string
  message: string
  confirmLabel: string
  onCancel: () => void
  onConfirm: () => void
  confirming: boolean
}) {
  const { t } = useLanguage()
  const palette = tone === "danger"
    ? { bg: "bg-red-50", text: "text-red-600", btn: "#DC2626" }
    : { bg: "bg-amber-50", text: "text-amber-700", btn: "#D97706" }

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-md">
        <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
          <div className="mb-3 flex items-start gap-3">
            <div className={`grid h-10 w-10 place-items-center rounded-xl ${palette.bg} ${palette.text} ring-1 ring-black/10`}>
              <WarningIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[16px] font-semibold text-gray-900">{title}</h3>
              <p className="mt-1 text-sm text-gray-600">{message}</p>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button type="button" className={`px-4 py-2 text-sm ${QUIET}`} onClick={onCancel}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              disabled={confirming}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white"
              style={{ background: palette.btn }}
              onClick={onConfirm}
            >
              {confirming ? t("common.working") : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Chip({
  children,
  tone,
}: {
  children: React.ReactNode
  tone?: PollStatus
}) {
  const styles: Record<string, React.CSSProperties> = {
    LIVE: { background: "rgba(39,174,96,0.12)", color: "#166534" },
    SCHEDULED: { background: "rgba(245,158,11,0.12)", color: "#78350F" },
    CLOSED: { background: "rgba(156,163,175,0.20)", color: "#374151" },
    default: { background: "rgba(31,58,147,0.10)", color: "#1F3A93" },
  }
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium" style={tone ? styles[tone] : styles.default}>
      {children}
    </span>
  )
}

function EmptyState({ onNew }: { onNew: () => void }) {
  const { t } = useLanguage()
  return (
    <div className="rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-8 text-center shadow-lg shadow-black/10">
      <div className="mx-auto mb-3 grid size-12 place-items-center rounded-2xl bg-white ring-1 ring-black/10 text-gray-800">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M4 19V5h16v14M8 9h8M8 13h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </div>
      <h3 className="text-[16px] font-semibold text-gray-900">{t("hoa.polls.emptyTitle")}</h3>
      <p className="mt-1 text-sm text-gray-600">{t("hoa.polls.emptyDesc")}</p>
      <button className={`mt-4 px-4 py-2 ${QUIET}`} onClick={onNew}>
        {t("hoa.polls.new")}
      </button>
    </div>
  )
}

function CreatePollModal({
  onClose,
  onCreate,
  creating,
}: {
  onClose: () => void
  onCreate: (payload: any) => void
  creating: boolean
}) {
  const { t } = useLanguage()
  const { data: buildings, isLoading: loadingBuildings } = useBuildings()
  const [building, setBuilding] = React.useState<string>("")
  const [title, setTitle] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [options, setOptions] = React.useState<string[]>(() => [t("common.yes"), t("common.no")])
  const [startAt, setStartAt] = React.useState<string>("")
  const [endAt, setEndAt] = React.useState<string>("")
  const [status, setStatus] = React.useState<PollStatus>("SCHEDULED")
  const [anonymous, setAnonymous] = React.useState(true)
  const [allowMultiple, setAllowMultiple] = React.useState(false)
  const [allowChangeVote, setAllowChangeVote] = React.useState(false)
  const [quorumPercent, setQuorumPercent] = React.useState<number | "">("")
  const [totalEligible, setTotalEligible] = React.useState<number | "">("")
  
  // Auto-select first building if only one available
  React.useEffect(() => {
    if (buildings && buildings.length === 1 && !building) {
      setBuilding(buildings[0].id)
    }
  }, [buildings, building])

  function pushOption() { setOptions((arr) => [...arr, t("hoa.polls.optionLabel", { index: arr.length + 1 })]) }
  function removeOption(i: number) { setOptions((arr) => arr.filter((_, idx) => idx !== i)) }
  function updateOption(i: number, v: string) { setOptions((arr) => arr.map((x, idx) => (idx === i ? v : x))) }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!building || !title.trim() || options.filter((o) => o.trim()).length < 2) return
    onCreate({
      building, // Required FK to Building/Condo
      title: title.trim(),
      description: description.trim() || undefined,
      options: options.filter((o) => o.trim()).map((label) => ({ label })),
      startAt: startAt ? new Date(startAt).toISOString() : undefined,
      endAt: endAt ? new Date(endAt).toISOString() : undefined,
      status,
      anonymous,
      allowMultiple,
      allowChangeVote,
      quorumPercent: quorumPercent === "" ? undefined : Number(quorumPercent),
      totalEligible: totalEligible === "" ? undefined : Number(totalEligible),
    })
  }

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-2xl">
        <form onSubmit={submit} className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
          <div className="flex items-center justify-between">
            <h3 className="text-[16px] font-semibold text-gray-900">{t("hoa.polls.createTitle")}</h3>
            <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
              {t("common.close")}
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Building Selector */}
            <div className="sm:col-span-2">
              <label className="text-sm">{t("hoa.polls.buildingRequired")}</label>
              {loadingBuildings ? (
                <div className="mt-1 w-full rounded-2xl border border-black/10 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  {t("hoa.polls.loadingBuildings")}
                </div>
              ) : buildings && buildings.length > 0 ? (
                <select
                  value={building}
                  onChange={(e) => setBuilding(e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                  required
                >
                  <option value="">{t("hoa.polls.selectBuilding")}</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} {b.code ? `(${b.code})` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mt-1 w-full rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {t("hoa.polls.noBuildings")}
                </div>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm">{t("common.title")}</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                placeholder={t("hoa.polls.titlePlaceholder")}
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm">{t("hoa.polls.descriptionOptional")}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                rows={3}
                placeholder={t("hoa.polls.descriptionPlaceholder")}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm">{t("hoa.polls.options")}</label>
              <div className="mt-2 space-y-2">
                {options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={opt}
                      onChange={(e) => updateOption(i, e.target.value)}
                      className="w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                      placeholder={t("hoa.polls.optionLabel", { index: i + 1 })}
                    />
                    <button
                      type="button"
                      className={`px-2 py-2 text-sm ${QUIET}`}
                      onClick={() => removeOption(i)}
                      disabled={options.length <= 2}
                      title={t("hoa.polls.removeOption")}
                    >
                      −
                    </button>
                  </div>
                ))}
                <button type="button" className={`px-3 py-2 ${QUIET}`} onClick={pushOption}>
                  {t("hoa.polls.addOption")}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm">{t("hoa.polls.start")}</label>
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>
            <div>
              <label className="text-sm">{t("hoa.polls.end")}</label>
              <input
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>

            <div>
              <label className="text-sm">{t("common.status")}</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as PollStatus)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              >
                <option value="SCHEDULED">{t("hoa.polls.status.scheduled")}</option>
                <option value="LIVE">{t("hoa.polls.status.live")}</option>
                <option value="CLOSED">{t("hoa.polls.status.closed")}</option>
              </select>
            </div>

            <div>
              <label className="text-sm">{t("hoa.polls.quorumOptional")}</label>
              <input
                type="number"
                min={0}
                max={100}
                value={quorumPercent}
                onChange={(e) => setQuorumPercent(e.target.value === "" ? "" : Number(e.target.value))}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
                placeholder={t("hoa.polls.quorumPlaceholder")}
              />
            </div>

            <div>
              <label className="text-sm">{t("hoa.polls.totalEligibleOptional")}</label>
              <input
                type="number"
                min={0}
                value={totalEligible}
                onChange={(e) => setTotalEligible(e.target.value === "" ? "" : Number(e.target.value))}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>

            <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="h-4 w-4" />
                {t("hoa.polls.anonymousVoting")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} className="h-4 w-4" />
                {t("hoa.polls.allowMultiple")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={allowChangeVote} onChange={(e) => setAllowChangeVote(e.target.checked)} className="h-4 w-4" />
                {t("hoa.polls.allowChangeVote")}
              </label>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button type="button" className={`px-4 py-2 text-sm ${QUIET}`} onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={creating}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px]"
              style={{ background: "linear-gradient(90deg, #1F3A93 0%, #1F3A93 70%, #27AE60 100%)" }}
            >
              {creating ? t("hoa.polls.creating") : t("hoa.polls.createAction")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SkeletonPoll() {
  return (
    <div className="animate-pulse rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-5 shadow-lg shadow-black/10">
      <div className="h-4 w-1/3 rounded bg-gray-200" />
      <div className="mt-2 h-3 w-2/3 rounded bg-gray-200" />
      <div className="mt-4 space-y-2">
        <div className="h-2 w-full rounded bg-gray-200" />
        <div className="h-2 w-5/6 rounded bg-gray-200" />
      </div>
    </div>
  )
}

function fmtDateTime(iso?: string) {
  if (!iso) return "-"
  const d = new Date(iso)
  return d.toLocaleString()
}
function toLocalInput(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => n.toString().padStart(2, "0")
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

/** Icons */
function WarningIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 8v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}
